@if (preview(); as review) {
    <p role="status" class="sr-only">
        Preview ready. Review your changes before creating the pack.
    </p>
    <section
        class="grid min-w-0 gap-4 p-4 sm:p-5"
        [attr.aria-label]="`Review ${review.packName}`"
    >
        <div class="flex min-w-0 flex-wrap items-start justify-between gap-2">
            <h4
                #reviewHeading
                qpxHeading
                variant="lg"
                tabindex="-1"
                [attr.aria-label]="review.packName"
                [qpxTooltip]="review.packName"
                class="m-0 min-w-0 basis-full truncate focus-visible:outline-2 focus-visible:outline-offset-2 sm:flex-1 sm:basis-0"
            >
                {{ review.packName }}
            </h4>
        </div>
        <div class="grid gap-2 min-[768px]:gap-3">
            <div class="flex items-center gap-4">
                <div class="flex items-center gap-1.5">
                    <mat-progress-spinner
                        class="progress-ring"
                        [class.progress-default]="progress() === 0"
                        [class.progress-success]="progress() === 100"
                        mode="determinate"
                        [diameter]="16"
                        [strokeWidth]="2"
                        [value]="progress()"
                        [attr.aria-label]="`${covered()} of ${review.rows.length} requirements covered; ${progress()}% complete`"
                    /><span
                        qpxCell="secondary"
                        tone="muted"
                        variant="sm"
                        class="whitespace-nowrap"
                        >{{ progress() }}% complete</span
                    >
                </div>
                <qpx-pill
                    size="sm"
                    [label]="status().label"
                    [context]="status().context"
                    [tone]="status().tone"
                />
            </div>
        </div>
        @if (review.command.description) {
            <section
                class="rounded-[var(--qpx-radius-base)] border border-border bg-card text-card"
                aria-label="Pack description"
            >
                <h5
                    qpxColHeader
                    class="m-0 px-6 py-2 uppercase text-foreground!"
                >
                    Description
                </h5>
                <p
                    qpxBody="secondary"
                    class="m-0 px-6 pb-4 whitespace-pre-line break-words line-clamp-3"
                >
                    {{ review.command.description }}
                </p>
            </section>
        }
        <div class="grid gap-4">
            @for (group of groups(); track $index) {
                <details
                    open
                    class="section-card overflow-hidden rounded-lg border border-border bg-card text-card"
                >
                    <summary
                        tabindex="0"
                        #coverageTooltip="qpxTooltip"
                        [qpxTooltip]="group.coverage.label"
                        (focus)="coverageTooltip.show()"
                        (blur)="coverageTooltip.hide()"
                        class="section-heading flex min-h-12 cursor-pointer items-center justify-between gap-2 border-b border-border bg-accent py-2.5 pr-7"
                    >
                        <span class="flex min-w-0 items-center gap-1"
                            ><ng-icon
                                qpx
                                size="sm"
                                class="collapse-icon"
                                name="lucideChevronDown"
                            />
                            <h5
                                qpxCell
                                class="m-0 min-w-0 truncate font-semibold"
                            >
                                {{ group.name }}
                            </h5></span
                        >
                        <span
                            qpxCell="secondary"
                            tone="muted"
                            variant="sm"
                            class="flex shrink-0 items-center gap-1.5 whitespace-nowrap"
                        >
                            <ng-icon
                                qpx
                                size="sm"
                                [name]="group.coverage.icon"
                                [context]="group.coverage.context"
                                [tone]="
                                    group.coverage.context === 'secondary'
                                        ? 'muted'
                                        : 'default'
                                "
                            />
                            <span class="coverage-label">{{
                                group.coverage.label
                            }}</span></span
                        >
                    </summary>
                    <div
                        class="column-headings uppercase border-b border-border py-2"
                        aria-hidden="true"
                    >
                        <span qpxColHeader>Requirement</span
                        ><span qpxColHeader>Assigned documents</span>
                    </div>
                    <ul class="m-0 list-none divide-y divide-border p-0">
                        @for (row of group.rows; track $index) {
                            <li
                                class="requirement-row grid min-w-0 gap-2 px-7 py-3"
                            >
                                <div class="min-w-0 grid content-start gap-1">
                                    <p qpxCell class="m-0 font-semibold">
                                        {{ row.requirementName }}
                                    </p>
                                    @if (row.description) {
                                        <p
                                            qpxCell="secondary"
                                            tone="muted"
                                            variant="sm"
                                            class="m-0 line-clamp-2"
                                        >
                                            {{ row.description }}
                                        </p>
                                    }
                                </div>
                                <div
                                    class="flex min-w-0 flex-wrap content-start items-center gap-1"
                                >
                                    @for (
                                        document of row.documents;
                                        track document.id
                                    ) {
                                        <qpx-quippy-pack-assigned-document
                                            [fileName]="document.name"
                                            [buildingId]="
                                                review.command.buildingId
                                            "
                                            [documentId]="document.id"
                                        />
                                    } @empty {
                                        <button
                                            qpxButton
                                            matButton="outlined"
                                            size="sm"
                                            context="secondary"
                                            tone="muted"
                                            disabled
                                            class="no-disabled-color border-dashed!"
                                        >
                                            <ng-icon
                                                qpx
                                                matButtonIcon
                                                size="sm"
                                                name="lucideCircleAlert"
                                                context="secondary"
                                            />No documents assigned
                                        </button>
                                    }
                                </div>
                            </li>
                        }
                    </ul>
                </details>
            }
        </div>
        <qpx-quippy-pack-review-actions
            (editClicked)="editClicked.emit()"
            (applyClicked)="applyClicked.emit()"
        />
    </section>
}
